import type { RegistrationFieldItem } from "@moment-golf/types";

/** Minimal event shape needed to resolve event-sourced registration field options */
export interface EventForRegistrationOptions {
  flightPricing?: boolean;
  flighting?: {
    divisions?: Array<Record<string, unknown>>;
    flights?: Array<Record<string, unknown>>;
  };
}

export interface RegistrationOption {
  label: string;
  value: string;
}

/** Optional context for event-sourced fields (e.g. selected division to filter flights). */
export interface RegistrationFieldOptionsContext {
  selectedDivision?: string | null;
}

/**
 * Returns select options for a registration field. For event-sourced fields (division, flight),
 * options are resolved from the event; otherwise uses the field's static options.
 * Stored values are names (divisionName / flightName), not indices.
 */
export function getRegistrationFieldOptions(
  field: RegistrationFieldItem,
  event: EventForRegistrationOptions | null | undefined,
  context?: RegistrationFieldOptionsContext
): RegistrationOption[] {
  if (field.eventSource && event?.flighting) {
    const labelField =
      field.eventLabelField ??
      (field.eventSource === "division" ? "divisionName" : "flightName");

    let raw: Array<Record<string, unknown>>;
    if (field.eventSource === "division") {
      raw = event.flighting.divisions ?? [];
    } else {
      raw = event.flighting.flights ?? [];
      const selectedDivision = context?.selectedDivision;
      if (selectedDivision != null && selectedDivision !== "") {
        const divisions = event.flighting.divisions ?? [];
        const divisionIndex = divisions.findIndex(
          (d) => String(d.divisionName ?? "") === String(selectedDivision)
        );
        if (divisionIndex >= 0) {
          raw = raw.filter((f) => (f.divisionIndex as number) === divisionIndex);
        }
      }
    }

    const showPrice = event.flightPricing === true;
    const priceField =
      field.eventSource === "division" ? "divisionPrice" : "flightPrice";

    return raw
      .map((item) => {
        const label = item[labelField];
        const str =
          typeof label === "string" ? label : label != null ? String(label) : "";
        if (!str) return null;
        const price =
          showPrice && typeof item[priceField] === "number"
            ? (item[priceField] as number)
            : null;
        const displayLabel = price != null ? `${str} ($${price})` : str;
        return { label: displayLabel, value: str };
      })
      .filter((o): o is RegistrationOption => o != null);
  }
  const opts = field.options ?? [];
  return opts.map((opt) => ({ label: opt, value: opt }));
}

/** Division names configured on the event, for grid dropdowns. */
export function getDivisionNameOptions(
  event: EventForRegistrationOptions | null | undefined
): RegistrationOption[] {
  return (event?.flighting?.divisions ?? [])
    .map((item) => {
      const name = item.divisionName;
      const str = typeof name === "string" ? name : name != null ? String(name) : "";
      return str ? { label: str, value: str } : null;
    })
    .filter((o): o is RegistrationOption => o != null);
}

/** Flight names for a division (or all flights when division is unset). */
export function getFlightNameOptions(
  event: EventForRegistrationOptions | null | undefined,
  selectedDivision?: string | null
): RegistrationOption[] {
  const flightField: RegistrationFieldItem = {
    name: "flight",
    label: "Flight",
    field: "flight",
    type: "select",
    required: false,
    eventSource: "flight",
    eventLabelField: "flightName",
  };
  return getRegistrationFieldOptions(flightField, event, { selectedDivision });
}

/** True when the flight name belongs to the given division on the event. */
export function isFlightValidForDivision(
  event: EventForRegistrationOptions | null | undefined,
  divisionName: string | null | undefined,
  flightName: string | null | undefined
): boolean {
  if (!flightName) return true;
  const options = getFlightNameOptions(event, divisionName ?? null);
  return options.some((o) => o.value === flightName);
}
